import { useMutation } from '@tanstack/react-query';
import { Col, Form, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { DefaultOptionType } from 'antd/es/select';
import { PlantApi } from 'apis/PlantApis';
import ProductApi from 'apis/ProductApis';
import productType from 'apis/ProductType';
import UnitApi from 'apis/UnitApi';
import { FormFilter, InputForm, SelectForm } from 'components';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import Content from 'layouts/Content';
import { sharedProps } from 'pages/Plant/SaleLine/FilterSaleLinePage';
import { KeyboardEvent, useCallback, useEffect, useState } from 'react';

const spanCol = 8;
interface IProps {
  defaultFilter: any;
  handleOkFiltered: (values: FormValues) => void;
  handleCancelFiltered: () => void;
}

export type ComboBoxOption = {
  value: string | number;
  label: string;
};
export interface ApiResponse<T> {
  data: {
    [key: string]: {
      items?: T[];
    };
  };
}

interface FormValues {
  plantId?: string | number;
  productId?: string;
  productName?: string;
  sloc?: string | number;
  batch?: string | number;
  age?: any;
  unit?: string;
  groupPrice?: string | number;
}

export default function FilterProductSloc({
  defaultFilter,
  handleOkFiltered,
  handleCancelFiltered,
}: IProps) {
  const { user } = useUser();
  const [form] = useForm();
  const [dataUnit, setDataUnit] = useState<ComboBoxOption[]>([]);
  const [dataProductType, setDataProductType] = useState<ComboBoxOption[]>([]);
  const [dataPlant, setDataPlant] = useState<ComboBoxOption[]>([]);
  const [age, setAge] = useState<ILazySelectData>(initLazySelectData);
  const [sloc, setSloc] = useState<ILazySelectData>(initLazySelectData);
  const [batch, setBatch] = useState<ILazySelectData>(initLazySelectData);
  const [groupPrice, setGroupPrice] = useState<ComboBoxOption[]>([]);

  const onFinish = (values: FormValues) => {
    const ageToArray = Array.isArray(values.age)
      ? values.age.map((num: number) => num.toString())
      : values.age?.toString();

    const trimmedValues = {
      ...values,
      productId: values.productId ? values.productId.trim() : undefined,
      productName: values.productName ? values.productName.trim() : undefined,
      age: ageToArray,
      unit: values.unit,
    };

    handleOkFiltered(trimmedValues);
  };

  const handleCancel = () => {
    form.resetFields();
    form.setFieldsValue(defaultFilter);

    handleCancelFiltered();
  };

  const { mutate: getAgeComboBox } = useMutation({
    mutationFn: (params: { pageSize: number; age?: number }) =>
      ProductApi.getAgeCombobox(params),
    onSuccess: (res) => {
      if (res?.data?.getProduct)
        setAge({
          options: (res?.data?.getProduct?.items ?? []).map(
            (item: any) =>
              ({
                value: item?.age,
                title: item?.age,
              }) as DefaultOptionType,
          ),
          totalItems: res?.data?.getProduct?.pageInfo?.totalItems ?? 0,
        });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: getAllUnit } = useMutation({
    mutationFn: () => UnitApi.getAllUnit(),
    onSuccess: (res: ApiResponse<{ unit: string }>) => {
      setDataUnit(
        res.data.getProduct.items
          ? res.data.getProduct.items.map((item) => ({
              value: item.unit,
              label: item.unit,
            }))
          : [],
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: getPlant } = useMutation({
    mutationFn: () => PlantApi.getAllPlant(),
    onSuccess: (res: ApiResponse<{ id: string }>) => {
      setDataPlant(
        res.data.getPlant.items
          ? res.data.getPlant.items.map((item) => ({
              value: item.id,
              label: item.id,
            }))
          : [],
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: getAllProductType } = useMutation({
    mutationFn: () => productType.getAllProductType(),
    onSuccess: (res: ApiResponse<{ name: string; id: string }>) => {
      setDataProductType(
        res.data.getCode.items
          ? res.data.getCode.items
              .filter(
                (item) =>
                  item.name.toLowerCase() !== 'hàng thu dẻ' &&
                  item.name.toLowerCase() !== 'thu dẻ',
              )
              .map((item) => ({
                value: item.id,
                label: item.name,
              }))
          : [],
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: getSlocComboBox } = useMutation({
    mutationFn: (params: { pageSize: number; sloc?: string }) =>
      ProductApi.getSlocCombobox(params),
    onSuccess: (res) => {
      if (res?.data?.getStorageLocation)
        setSloc({
          options: (res?.data?.getStorageLocation?.items ?? []).map(
            (item: any) =>
              ({
                value: item?.sloc,
                title: item?.sloc,
              }) as DefaultOptionType,
          ),
          totalItems: res?.data?.getStorageLocation?.pageInfo?.totalItems ?? 0,
        });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: getBatchComboBox } = useMutation({
    mutationFn: (params: { pageSize: number; batch?: string }) =>
      ProductApi.getBatchCombobox(params),
    onSuccess: (res) => {
      if (res?.data?.getStorageLocation)
        setBatch({
          options: (res?.data?.getStorageLocation?.items ?? []).map(
            (item: any) =>
              ({
                value: item?.batch,
                title: item?.batch,
              }) as DefaultOptionType,
          ),
          totalItems: res?.data?.getStorageLocation?.pageInfo?.totalItems ?? 0,
        });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: getGroupPriceCombobox } = useMutation({
    mutationFn: () => ProductApi.getGroupPriceCombobox(),
    onSuccess: (res: ApiResponse<{ groupPrice: string }>) => {
      setGroupPrice(
        res.data.getMarketGoldPrice.items
          ? res.data.getMarketGoldPrice.items
              .filter(
                (item) => item.groupPrice !== '' && item.groupPrice !== null,
              )
              .map((item) => ({
                value: item.groupPrice,
                label: item.groupPrice,
              }))
          : [],
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onFinish(form.getFieldsValue());
      e.preventDefault();
    }
  };

  useEffect(() => {
    getPlant();
    getAllUnit();
    getGroupPriceCombobox();
    getAllProductType();
  }, []);

  useEffect(() => {
    form.setFieldsValue(defaultFilter);
  }, [defaultFilter, form]);

  const handleSelectScrollBatchChange = useCallback(
    (params: ILazySelectParams) => {
      const { pageSize, value } = params;

      getBatchComboBox({
        pageSize,
        batch: value,
      });
    },
    [],
  );

  const handleSelectScrollSlocChange = useCallback(
    (params: ILazySelectParams) => {
      const { pageSize, value } = params;

      getSlocComboBox({
        pageSize,
        sloc: value,
      });
    },
    [],
  );

  const handleSelectScrollAgeChange = useCallback(
    (params: ILazySelectParams) => {
      const { pageSize, value } = params;

      getAgeComboBox({
        pageSize,
        age: Number(value),
      });
    },
    [],
  );
  return (
    <Content>
      <FormFilter form={form} onCancel={handleCancel} onFinish={onFinish}>
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Chi nhánh" name="plantId">
              <SelectForm
                disabled={!(user?.isAdmin || user?.isHO)}
                placeholder="Chọn chi nhánh"
                options={dataPlant}
              />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Mã sản phẩm" name="productId">
              <InputForm
                onKeyDown={handleKeyDown}
                placeholder="Nhập mã sản phẩm"
              />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Tên sản phẩm" name="productName">
              <InputForm
                onKeyDown={handleKeyDown}
                placeholder="Nhập tên sản phẩm"
              />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Số Sloc" name="sloc">
              {/* <SelectForm
                {...sharedProps}
                placeholder="Chọn số sloc"
                options={sloc}
              /> */}
              <LazySelectForm
                // {...sharedProps}
                mode="multiple"
                data={sloc}
                placeholder="Chọn sloc"
                onSelectScrollChange={handleSelectScrollSlocChange}
              />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Số Batch" name="batch">
              {/* <SelectForm placeholder="Chọn số batch" options={batch} /> */}
              <LazySelectForm
                data={batch}
                placeholder="Chọn số batch"
                onSelectScrollChange={handleSelectScrollBatchChange}
              />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Tuổi vàng" name="age">
              {/* <SelectForm
                {...sharedProps}
                placeholder="Chọn tuổi vàng"
                options={age}
              /> */}
              <LazySelectForm
                // {...sharedProps}
                mode="multiple"
                data={age}
                placeholder="Chọn tuổi vàng"
                onSelectScrollChange={handleSelectScrollAgeChange}
              />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Đơn vị tính" name="unit">
              <SelectForm placeholder="Chọn đơn vị tính" options={dataUnit} />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Nhóm giá" name="groupPrice">
              <SelectForm placeholder="Chọn nhóm giá" options={groupPrice} />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Loại vàng" name="type">
              <SelectForm
                placeholder="Chọn loại vàng"
                options={dataProductType}
              />
            </Form.Item>
          </Col>
        </Row>
      </FormFilter>
    </Content>
  );
}
